/**
 * Field-level unit pairs. The stored number is always the America unit.
 * `factor` converts America -> the other unit (multiply). Reverse divides.
 * Save keeps 2 decimal places. `decimals` is the on-screen rounding, chosen
 * so a typed 5 still shows as 5 after that save. 10 C stores 50 F.
 */

export type FieldUnitPairId =
  | 'lengthFt'
  | 'lengthIn'
  | 'floorWeight'
  | 'temperature';

export type ChipSide = 'america' | 'other';

export interface FieldUnitPair {
  id: FieldUnitPairId;
  factor: number;
  decimals: number;
  americaLabel: string;
  otherLabel: string;
  americaName: string;
  otherName: string;
}

export const FIELD_UNIT_PAIRS: Record<FieldUnitPairId, FieldUnitPair> = {
  lengthFt: {
    id: 'lengthFt',
    factor: 0.3048,
    decimals: 2,
    americaLabel: 'ft',
    otherLabel: 'm',
    americaName: 'feet',
    otherName: 'meters',
  },
  lengthIn: {
    id: 'lengthIn',
    factor: 25.4,
    decimals: 0,
    americaLabel: 'in',
    otherLabel: 'mm',
    americaName: 'inches',
    otherName: 'millimeters',
  },
  floorWeight: {
    id: 'floorWeight',
    factor: 4.88242764,
    decimals: 1,
    americaLabel: 'lbs/sqft',
    otherLabel: 'kg/m2',
    americaName: 'pounds per square foot',
    otherName: 'kilograms per square meter',
  },
  temperature: {
    id: 'temperature',
    factor: 5 / 9,
    decimals: 1,
    americaLabel: 'F',
    otherLabel: 'C',
    americaName: 'fahrenheit',
    otherName: 'celsius',
  },
};

function otherFromAmerica(amount: number, pair: FieldUnitPair): number {
  if (pair.id === 'temperature') return ((amount - 32) * 5) / 9;
  return amount * pair.factor;
}

function americaFromOther(amount: number, pair: FieldUnitPair): number {
  if (pair.id === 'temperature') return (amount * 9) / 5 + 32;
  return amount / pair.factor;
}

export function roundTo(value: number, decimals: number): number {
  const scale = 10 ** decimals;
  return Math.round(value * scale) / scale;
}

export function formatUnitNumber(value: number, decimals: number): string {
  if (!Number.isFinite(value)) return '';
  const fixed = roundTo(value, decimals).toFixed(decimals);
  return fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
}

/** Digits kept on the stored America number. Matches the 2 decimals save keeps. */
export const STORED_AMERICA_DECIMALS = 2;

export function otherSide(side: ChipSide): ChipSide {
  return side === 'america' ? 'other' : 'america';
}

export function chipLabel(pairId: FieldUnitPairId, side: ChipSide): string {
  const pair = FIELD_UNIT_PAIRS[pairId];
  return side === 'america' ? pair.americaLabel : pair.otherLabel;
}

/** Drop baked-in `[ft]` / `[ft/in]` / `(ft)` so view mode does not show two units. */
const TRAILING_UNIT_SUFFIX =
  /(?:\s*[\[(](?:ft\/in|lbs\/sqft|lb\/sqft|kg\/m2|ft|in|mm|m|F|C)[\])])+$/i;

export function stripTrailingUnitSuffixes(label: string): string {
  return label.replace(TRAILING_UNIT_SUFFIX, '').trim();
}

/**
 * View mode cannot switch units, so the chip's unit sits on the label.
 * Edit mode keeps the chip and leaves the label alone.
 */
export function labelWithStoredUnit(
  label: string,
  pairId: FieldUnitPairId,
  showUnit: boolean,
  side: ChipSide = 'america',
): string {
  if (!showUnit) return label;
  return `${stripTrailingUnitSuffixes(label)} (${chipLabel(pairId, side)})`;
}

export function chipUnitName(pairId: FieldUnitPairId, side: ChipSide): string {
  const pair = FIELD_UNIT_PAIRS[pairId];
  return side === 'america' ? pair.americaName : pair.otherName;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Placeholder copy is written for the America unit. Follow the chip:
 * "in feet" becomes "in meters", "(ft)" becomes "(m)". A placeholder that
 * never names a unit gains "in inches" / "in millimeters" so it matches the chip.
 */
export function placeholderForChip(
  placeholder: string | undefined,
  pairId: FieldUnitPairId,
  side: ChipSide,
): string | undefined {
  if (!placeholder) return placeholder;
  const pair = FIELD_UNIT_PAIRS[pairId];
  const name = side === 'america' ? pair.americaName : pair.otherName;
  if (new RegExp(escapeRegExp(pair.americaName), 'i').test(placeholder)) {
    return side === 'america'
      ? placeholder
      : placeholder.replace(
          new RegExp(escapeRegExp(pair.americaName), 'ig'),
          pair.otherName,
        );
  }
  const labelPattern = `(?<![A-Za-z0-9])${escapeRegExp(
    pair.americaLabel,
  )}(?![A-Za-z0-9])`;
  if (new RegExp(labelPattern).test(placeholder)) {
    const nextLabel = side === 'america' ? pair.americaLabel : pair.otherLabel;
    return side === 'america'
      ? placeholder
      : placeholder.replace(new RegExp(labelPattern, 'g'), nextLabel);
  }
  return `${placeholder} in ${name}`;
}

/** Display string for the stored America number. America echoes the stored text. */
export function toDisplay(
  americaText: string,
  side: ChipSide,
  pairId: FieldUnitPairId,
): string {
  if (side === 'america') return americaText;
  const trimmed = americaText.trim();
  if (trimmed === '') return '';
  const amount = Number(trimmed);
  if (!Number.isFinite(amount)) return '';
  const pair = FIELD_UNIT_PAIRS[pairId];
  return formatUnitNumber(otherFromAmerica(amount, pair), pair.decimals);
}

/**
 * America string to write from what the user sees.
 * America side keeps the typed text. The other side converts and keeps enough
 * digits that the on-screen number round-trips. Blank stays blank.
 */
export function americaFromDisplay(
  displayText: string,
  side: ChipSide,
  pairId: FieldUnitPairId,
): string {
  const trimmed = displayText.trim();
  if (trimmed === '' || trimmed === '.') return '';
  if (side === 'america') return trimmed;
  const amount = Number(trimmed);
  if (!Number.isFinite(amount)) return '';
  const pair = FIELD_UNIT_PAIRS[pairId];
  const exact = americaFromOther(amount, pair);
  const typedDisplay = formatUnitNumber(amount, pair.decimals);
  for (let digits = 0; digits <= STORED_AMERICA_DECIMALS; digits += 1) {
    const candidate = formatUnitNumber(exact, digits);
    if (toDisplay(candidate, 'other', pairId) === typedDisplay)
      return candidate;
  }
  return formatUnitNumber(exact, STORED_AMERICA_DECIMALS);
}
